import { createSelector, Selector } from '@ngxs/store';

import {
  ProfileInformationModel,
  SidebarGroup,
  UserPermissions,
} from '../../models';
import { SettingsState, SettingsStateModel } from '../settings.state';

export class ProfileSelectors {
  @Selector([ProfileSelectors._profileInformation])
  static profileInformation(
    profileInformation: ProfileInformationModel,
  ): ProfileInformationModel {
    return profileInformation;
  }

  @Selector([ProfileSelectors._lastLoginTime])
  static lastLoginTime(lastLoginTime: string): string {
    return lastLoginTime;
  }

  @Selector([ProfileSelectors._profileInformationAccessLevel])
  static profileInformationAccessLevel(
    profileInformationAccessLevel: UserPermissions,
  ): UserPermissions {
    return profileInformationAccessLevel;
  }

  @Selector([ProfileSelectors._sidebarMenu])
  static sidebarMenu(sidebarMenu: SidebarGroup[]): SidebarGroup[] {
    return sidebarMenu;
  }

  @Selector([ProfileSelectors._submitSettingsStatus])
  static submitSettingsStatus(submitSettingsStatus: boolean): boolean {
    return submitSettingsStatus;
  }

  @Selector([ProfileSelectors._userRoles])
  static userRoles(userRoles: string[]): string[] {
    return userRoles;
  }

  @Selector([ProfileSelectors._oscContactId])
  static oscContactId(oscContactId: string): string {
    return oscContactId;
  }

  static hasPermission(
    category: string,
    permission: keyof UserPermissions[string],
  ): (state: SettingsStateModel) => boolean {
    return createSelector(
      [SettingsState],
      (state: SettingsStateModel) =>
        !!state.information.accessLevel[category]?.[permission],
    );
  }

  @Selector([ProfileSelectors._profileDataLoading])
  static profileDataLoading(isLoading: boolean): boolean {
    return isLoading;
  }

  @Selector([ProfileSelectors._profileDataLoaded])
  static profileDataLoaded(loaded: boolean): boolean {
    return loaded;
  }

  @Selector([ProfileSelectors._hasProfileDataApiError])
  static hasProfileDataApiError(hasError: boolean): boolean {
    return hasError;
  }

  @Selector([SettingsState])
  private static _profileInformation(
    state: SettingsStateModel,
  ): ProfileInformationModel {
    return state.information;
  }

  @Selector([SettingsState])
  private static _lastLoginTime(state: SettingsStateModel): string {
    return state.userLastLoginTime;
  }

  @Selector([SettingsState])
  private static _profileInformationAccessLevel(
    state: SettingsStateModel,
  ): UserPermissions {
    return state.information.accessLevel;
  }

  @Selector([SettingsState])
  private static _sidebarMenu(state: SettingsStateModel): SidebarGroup[] {
    return state.information.sidebarMenu;
  }

  @Selector([SettingsState])
  private static _submitSettingsStatus(state: SettingsStateModel): boolean {
    return state.submitSettingsStatus;
  }

  @Selector([SettingsState])
  private static _userRoles(state: SettingsStateModel): string[] {
    return state.userRoles;
  }

  @Selector([SettingsState])
  private static _oscContactId(state: SettingsStateModel): string {
    return state.information.oscContactId;
  }

  @Selector([SettingsState])
  private static _profileDataLoading(state: SettingsStateModel): boolean {
    return !state.loadedStates.profileData;
  }

  @Selector([SettingsState])
  private static _profileDataLoaded(state: SettingsStateModel): boolean {
    return state.loadedStates.profileData;
  }

  @Selector([SettingsState])
  private static _hasProfileDataApiError(state: SettingsStateModel): boolean {
    const error = state.loadingErrors.profileData;

    if (!error || error.trim() === '') {
      return false;
    }

    return true;
  }
}
